import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { OutputFormat } from './wysiwyg/types';

export type TemplateFormatData = {
  outputFormat?: unknown;
  dpi?: unknown;
  content?: unknown;
};

export type TemplateFormatOption = {
  format: OutputFormat;
  dpi: number;
  label: 'PDF' | 'ZPL 203 DPI' | 'ZPL 300 DPI';
};

const FORMAT_OPTIONS: TemplateFormatOption[] = [
  { format: 'pdf', dpi: 72, label: 'PDF' },
  { format: 'zpl', dpi: 203, label: 'ZPL 203 DPI' },
  { format: 'zpl', dpi: 300, label: 'ZPL 300 DPI' },
];

function parseTemplateContent(content: unknown): Record<string, unknown> | null {
  if (content && typeof content === 'object') return content as Record<string, unknown>;
  if (typeof content !== 'string' || !content) return null;

  try {
    let parsed: unknown = JSON.parse(content);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return parsed && typeof parsed === 'object'
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
}

export function getTemplateFormatLabel(template: TemplateFormatData): TemplateFormatOption['label'] {
  const content = parseTemplateContent(template.content);
  const outputFormat = content?.outputFormat ?? template.outputFormat;
  const dpi = content?.dpi ?? template.dpi;

  if (String(outputFormat).toLowerCase() !== 'zpl') return 'PDF';
  return Number(dpi) === 300 ? 'ZPL 300 DPI' : 'ZPL 203 DPI';
}

export function getCloneFormatOptions(template: TemplateFormatData): TemplateFormatOption[] {
  const currentLabel = getTemplateFormatLabel(template);
  return FORMAT_OPTIONS.filter((option) => option.label !== currentLabel);
}

export function getCloneMenuPosition({
  triggerRect,
  menuWidth,
  menuHeight,
  viewportWidth,
  viewportHeight,
  alignment,
  boundaryRect,
}: {
  triggerRect: Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'>;
  menuWidth: number;
  menuHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  alignment: 'left' | 'right';
  boundaryRect?: Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'>;
}) {
  const viewportPadding = 8;
  const menuGap = 8;
  const viewportLeft = viewportPadding;
  const viewportRight = viewportWidth - viewportPadding;
  const viewportTop = viewportPadding;
  const viewportBottom = viewportHeight - viewportPadding;
  const boundedLeft = Math.max(viewportLeft, boundaryRect?.left ?? viewportLeft);
  const boundedRight = Math.min(viewportRight, boundaryRect?.right ?? viewportRight);
  const boundedTop = Math.max(viewportTop, boundaryRect?.top ?? viewportTop);
  const boundedBottom = Math.min(viewportBottom, boundaryRect?.bottom ?? viewportBottom);
  const leftBoundary = boundedRight - boundedLeft >= menuWidth ? boundedLeft : viewportLeft;
  const rightBoundary = boundedRight - boundedLeft >= menuWidth ? boundedRight : viewportRight;
  const topBoundary = boundedBottom - boundedTop >= menuHeight ? boundedTop : viewportTop;
  const bottomBoundary = boundedBottom - boundedTop >= menuHeight ? boundedBottom : viewportBottom;
  const preferredLeft = alignment === 'right' ? triggerRect.right - menuWidth : triggerRect.left;
  const left = Math.min(
    Math.max(preferredLeft, leftBoundary),
    Math.max(leftBoundary, rightBoundary - menuWidth),
  );
  const belowTop = triggerRect.bottom + menuGap;
  const preferredTop = belowTop + menuHeight <= bottomBoundary
    ? belowTop
    : triggerRect.top - menuHeight - menuGap;
  const top = Math.min(
    Math.max(preferredTop, topBoundary),
    Math.max(topBoundary, bottomBoundary - menuHeight),
  );
  return { left, top };
}

type TemplateFormatBadgeProps = {
  template: TemplateFormatData;
  interactive?: boolean;
  isOpen?: boolean;
  alignment?: 'left' | 'right';
  onOpenChange?: (open: boolean) => void;
  onCloneAs?: (option: TemplateFormatOption) => void;
};

export default function TemplateFormatBadge({
  template,
  interactive = false,
  isOpen = false,
  alignment = 'left',
  onOpenChange,
  onCloneAs,
}: TemplateFormatBadgeProps) {
  const label = getTemplateFormatLabel(template);
  const canInteract = interactive &&
    typeof onOpenChange === 'function' &&
    typeof onCloneAs === 'function';
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || [],
    );
    if (!items.length) return;

    event.preventDefault();
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'Home') {
      items[0].focus();
    } else if (event.key === 'End') {
      items[items.length - 1].focus();
    } else if (event.key === 'ArrowDown') {
      items[(currentIndex + 1) % items.length].focus();
    } else {
      items[(currentIndex - 1 + items.length) % items.length].focus();
    }
  };

  const positionMenu = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const boundaryRect = trigger.closest<HTMLElement>('.card, .tile-card')?.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth || 220;
    const menuHeight = menuRef.current?.offsetHeight || 124;
    setMenuPosition(getCloneMenuPosition({
      triggerRect: rect,
      menuWidth,
      menuHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      alignment,
      boundaryRect,
    }));
  };

  useLayoutEffect(() => {
    if (!canInteract || !isOpen) return;
    positionMenu();
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, [alignment, canInteract, isOpen]);

  useEffect(() => {
    if (!canInteract || !isOpen) return;

    const closeOnOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        onOpenChange?.(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onOpenChange?.(false);
      triggerRef.current?.focus();
    };
    const handleViewportChange = () => positionMenu();

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('touchstart', closeOnOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('touchstart', closeOnOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [canInteract, isOpen, onOpenChange]);

  if (!canInteract) {
    return <span className="tag subtle template-format-badge" title={label}>{label}</span>;
  }

  const menu = isOpen && typeof document !== 'undefined' ? createPortal(
    <div
      ref={menuRef}
      className="template-clone-menu"
      role="menu"
      aria-label={`Clone ${label} template as`}
      onKeyDown={handleMenuKeyDown}
      style={{ left: menuPosition.left, top: menuPosition.top }}
      data-testid="template-clone-menu"
    >
      <div className="template-clone-menu-label">Clone as</div>
      {getCloneFormatOptions(template).map((option) => (
        <button
          key={option.label}
          type="button"
          role="menuitem"
          className="template-clone-menu-item"
          onClick={() => {
            onOpenChange?.(false);
            onCloneAs?.(option);
            triggerRef.current?.focus();
          }}
          data-testid={`template-clone-as-${option.format}-${option.dpi}`}
        >
          <span className="template-clone-icon" aria-hidden="true">
            <span />
          </span>
          {option.label}
        </button>
      ))}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`tag subtle template-format-badge template-format-trigger${isOpen ? ' open' : ''}`}
        aria-label={`Format: ${label}, clone as another format`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => onOpenChange?.(!isOpen)}
        data-testid="template-format-menu-trigger"
      >
        <span>{label}</span>
        <svg className="template-format-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M3 4.5 6 7.5l3-3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {menu}
    </>
  );
}
